// A land's atmosphere on top of the normal day/night cycle: distance mist, a tinted sky and a colour grade on the lights. A land opts in with
// a `mood` block in its land.json (written from the build config's `mood` by scripts/adventure/make_adventure.mjs):
//   mood: {
//     fog:   { day: '#d6e6df', night: '#18283a', near: 21, far: 76 }     // mist; near/far are camera-space units (the cat sits ~17 away)
//     sky:   { day: ['#9ec3d6', '#dfeee9', '#f3f6ef'], night: [...], alpha: 0.7 }   // a wash over the normal sky gradient (top, middle, bottom)
//     light: { ambient: '#ffffff', ambientK: 1, sun: '#ffffff', sunK: 1, nightK: 1 }  // multipliers on the day/night lights
//   }
// The day/night system sets the lights every frame; this runs right after it, so the grade is re-applied each frame and never accumulates.
import * as THREE from 'three'

export function createLandMood({ scene, ambient, sun }) {
  let mood = null
  let sky = null
  const fogDay = new THREE.Color(), fogNight = new THREE.Color(), tint = new THREE.Color(), sunTint = new THREE.Color()
  const c1 = new THREE.Color(), c2 = new THREE.Color()
  let lastKey = ''

  const ensureSky = () => {
    if (sky) return sky
    sky = document.createElement('div')
    sky.id = 'land-mood-sky'
    sky.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:-1;opacity:0;transition:opacity 1.2s ease'
    const night = document.getElementById('sky-night') // sit just under the stars
    document.body.insertBefore(sky, night ?? document.body.firstChild)
    return sky
  }
  const mix = (a, b, t) => '#' + c1.set(a).lerp(c2.set(b), t).getHexString()

  function apply(m) {
    mood = m ?? null
    lastKey = ''
    if (!mood) { clear(); return }
    if (mood.fog) { fogDay.set(mood.fog.day); fogNight.set(mood.fog.night ?? mood.fog.day); scene.fog = new THREE.Fog(fogDay.getHex(), mood.fog.near ?? 20, mood.fog.far ?? 80) }
    if (mood.light?.ambient) tint.set(mood.light.ambient)
    if (mood.light?.sun) sunTint.set(mood.light.sun)
    if (mood.sky && typeof document !== 'undefined') ensureSky().style.opacity = String(mood.sky.alpha ?? 0.7)
  }
  function clear() {
    mood = null
    scene.fog = null
    if (sky) sky.style.opacity = '0'
  }
  // night: 0 (day) … 1 (night)
  function tick(night = 0) {
    if (!mood) return
    const n = Math.max(0, Math.min(1, night))
    if (scene.fog && mood.fog) scene.fog.color.copy(fogDay).lerp(fogNight, n)
    const L = mood.light
    if (L) {
      if (L.ambient) ambient.color.multiply(tint)
      if (L.sun) sun.color.multiply(sunTint)
      const k = 1 + ((L.nightK ?? 1) - 1) * n
      ambient.intensity *= (L.ambientK ?? 1) * k
      sun.intensity *= (L.sunK ?? 1) * k
    }
    if (sky && mood.sky) {
      const d = mood.sky.day, nt = mood.sky.night ?? mood.sky.day
      const key = `${n.toFixed(2)}`
      if (key !== lastKey) {
        lastKey = key
        sky.style.background = `linear-gradient(180deg, ${mix(d[0], nt[0], n)} 0%, ${mix(d[1], nt[1], n)} 50%, ${mix(d[2], nt[2], n)} 100%)`
      }
    }
  }
  return { apply, clear, tick }
}
